import { useEffect } from 'react';
import { SITE, PAGES } from '../seoMeta.mjs';

/**
 * Sets, creates, or removes a head tag. Passing a null value removes the tag
 * (used to drop the canonical/og:url on 404 pages instead of leaving a stale
 * URL next to the noindex).
 */
const setTag = (selector, value, create) => {
  let el = document.head.querySelector(selector);
  if (value == null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = create(document);
    document.head.appendChild(el);
  }
  el.setAttribute(el.tagName === 'LINK' ? 'href' : 'content', value);
};

const meta = (attr, key) => [
  `meta[${attr}="${key}"]`,
  (d) => {
    const el = d.createElement('meta');
    el.setAttribute(attr, key);
    return el;
  },
];

const CANONICAL = ['link[rel="canonical"]', (d) => {
  const el = d.createElement('link');
  el.setAttribute('rel', 'canonical');
  return el;
}];

/**
 * Updates the document head on client-side navigation. The static tags this
 * mutates are baked into every route's HTML by scripts/prerender.mjs, so
 * crawlers see the same values without running JS.
 */
export default function usePageMeta(page) {
  useEffect(() => {
    if (!page) return;
    const url = page.path ? SITE.origin + page.path : null;
    const image = SITE.origin + (page.ogImage || PAGES.home.ogImage);
    const imageAlt = page.ogImageAlt || PAGES.home.ogImageAlt;

    document.title = page.title;
    const set = ([sel, create], value) => setTag(sel, value, create);
    set(meta('name', 'description'), page.description);
    set(meta('name', 'robots'), page.noindex ? 'noindex' : SITE.defaultRobots);
    set(CANONICAL, url);
    set(meta('property', 'og:title'), page.title);
    set(meta('property', 'og:description'), page.description);
    set(meta('property', 'og:url'), url);
    set(meta('property', 'og:image'), image);
    set(meta('property', 'og:image:alt'), imageAlt);
    set(meta('name', 'twitter:title'), page.title);
    set(meta('name', 'twitter:description'), page.description);
    set(meta('name', 'twitter:image'), image);
    set(meta('name', 'twitter:image:alt'), imageAlt);
  }, [page]);
}
